import Domain from "@mui/icons-material/Domain";
import ExpandMore from "@mui/icons-material/ExpandMore";
import WorkOffOutlined from "@mui/icons-material/WorkOffOutlined";
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { SystemBanner } from "../../../../../../components/UIToolkit/molecules/SystemBanner";
import { useAnalytics } from "../../../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../../../contexts/HospitalDataProvider";
import { hrisEmployeeName } from "../../../../../../utils/formatters";
import type { HrisEmployeeShiftStats, Job, JobNotEnabledEmployee } from "../../../../../../utils/models";
import type { JobNotEnabledMark } from "./EmployeeSyncStatusIcon";
import type { JobPickerChange } from "./JobPickerCell";
import { PaneHeader } from "./PaneHeader";
import { RoleStringRowItem } from "./RoleStringRowItem";
import { SyncStatusIcon } from "./SyncStatusIcon";
import { countSyncingEmployees } from "./syncStatus";
import type { RoleStringRow } from "./useRoleStringRows";

interface HrisRawEmployeesProps {
	rows: RoleStringRow[];
	jobs: Job[];
	/** Set of roleStrings whose save is debounced/in flight — drives the per-row spinner. */
	pendingKeys: Set<string>;
	onJobChange: (roleString: string, change: JobPickerChange) => void;
	/** Fires with a row's new additional jobs (jobs[1:]) when the admin confirms the additional-jobs dialog (DAT-466). */
	onAdditionalJobsChange: (roleString: string, additionalJobIds: string[]) => void;
	/** Per-employee activity stats keyed by employeeId — drives the per-employee shift-count hover popup. */
	statsByEmployeeId: Map<string, HrisEmployeeShiftStats>;
	/** Employees whose primary job this department doesn't enable, keyed by employeeId (DAT-834). */
	jobNotEnabledByEmployeeId: Map<string, JobNotEnabledMark>;
	/**
	 * Every employee the DEPARTMENT has in that state, including the ones this pane cannot show. The
	 * signal is nurse-side while this pane renders raw HRIS rows, so some flagged people have nothing
	 * to mark here — no HRIS record at all, or a raw row whose role string is blank, which
	 * `useRoleStringRows` drops. The banner counts those rather than quietly reporting a smaller
	 * number than the Jobs tab.
	 */
	departmentJobNotEnabled: JobNotEnabledEmployee[];
	/** Search state owned by the parent so both panes filter against the same query. */
	search: string;
	setSearch: (next: string) => void;
}

/** Bucket roles by internal_department for nested-accordion rendering. */
function groupByInternalDepartment(rows: RoleStringRow[]): [string, RoleStringRow[]][] {
	const map = new Map<string, RoleStringRow[]>();
	for (const row of rows) {
		// A role string can appear under multiple internal_departments — render it under each.
		const buckets = row.internalDepartments.length > 0 ? row.internalDepartments : [""];
		for (const dept of buckets) {
			let arr = map.get(dept);
			if (!arr) {
				arr = [];
				map.set(dept, arr);
			}
			arr.push(row);
		}
	}
	// Sort: real names alphabetically, empty group last.
	return Array.from(map.entries()).sort(([a], [b]) => {
		if (a === "" && b !== "") return 1;
		if (b === "" && a !== "") return -1;
		return a.localeCompare(b);
	});
}

/** Sentinel key for raw employees that have no `internal_department` value. */
const NO_DEPT_KEY = "__no_dept__";

/**
 * Left pane of the HRIS Job Alignment view. Lists raw HRIS employees grouped first by
 * internal_department, then by role_string, with a per-role-string job picker.
 *
 * Only role strings that actually have raw employees in this department appear here — this
 * pane mirrors the `hris_raw_employee` table for the department. Cert mappings without
 * matching raw employees (custom-only, unused defaults) live in the right-pane summary.
 *
 * Perf: per-row collapse state is owned by `RoleStringRowItem`, so expanding one row never
 * forces the rest of the pane to re-render.
 */
export const HrisRawEmployees: FC<HrisRawEmployeesProps> = ({
	rows,
	jobs,
	pendingKeys,
	onJobChange,
	onAdditionalJobsChange,
	statsByEmployeeId,
	jobNotEnabledByEmployeeId,
	departmentJobNotEnabled,
	search,
	setSearch,
}) => {
	const { t } = useTranslation();

	const rowsWithEmployees = useMemo(() => rows.filter((r) => r.rawEmployees.length > 0), [rows]);

	/**
	 * Flagged employees this pane actually renders an icon for — counted over the UNFILTERED rows, so
	 * typing in the search box narrows the list without rewriting the pane's headline number.
	 */
	const markedJobNotEnabledIds = useMemo(() => {
		const marked = new Set<string>();
		for (const row of rowsWithEmployees) {
			for (const employee of row.rawEmployees) {
				if (jobNotEnabledByEmployeeId.has(employee.employeeId)) marked.add(employee.employeeId);
			}
		}
		return marked;
	}, [rowsWithEmployees, jobNotEnabledByEmployeeId]);

	const markedJobNotEnabledCount = markedJobNotEnabledIds.size;
	/**
	 * The rest of the department's flagged employees, counted by asking which ones this pane did NOT
	 * mark — never `total − marked`. A subtraction can go negative if the two ever disagree, and the
	 * clamp that would hide that would delete the whole line instead of showing that the numbers don't
	 * reconcile.
	 */
	const unmarkedJobNotEnabledCount = useMemo(
		() => departmentJobNotEnabled.filter((employee) => employee.employeeId === null || !markedJobNotEnabledIds.has(employee.employeeId)).length,
		[departmentJobNotEnabled, markedJobNotEnabledIds],
	);

	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	// One view event per department per distinct count — a re-render or a search keystroke isn't a view.
	const trackedJobNotEnabledView = useRef<string | null>(null);
	useEffect(() => {
		if (markedJobNotEnabledCount === 0) return;
		const signature = `${chosenDepartment.department_id}:${markedJobNotEnabledCount}`;
		if (trackedJobNotEnabledView.current === signature) return;
		trackedJobNotEnabledView.current = signature;
		track("hris_job_not_enabled_banner_view", {
			department_id: chosenDepartment.department_id,
			count: markedJobNotEnabledCount,
			department_total: departmentJobNotEnabled.length,
		});
	}, [chosenDepartment.department_id, markedJobNotEnabledCount, departmentJobNotEnabled.length, track]);

	const filteredRows = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return rowsWithEmployees;
		return rowsWithEmployees.filter((r) => {
			if (r.roleString.toLowerCase().includes(q)) return true;
			return r.rawEmployees.some((e) => hrisEmployeeName(e).toLowerCase().includes(q));
		});
	}, [rowsWithEmployees, search]);

	const departmentGroups = useMemo(() => groupByInternalDepartment(filteredRows), [filteredRows]);

	// When search narrows the visible list to a single department, flag every role row in that
	// dept whose role string OR any employee name still matches the query. RoleStringRowItem
	// only opens on this flag, never auto-closes, so the user can still collapse manually.
	const autoExpandedRoleStrings = useMemo<Set<string>>(() => {
		const q = search.trim().toLowerCase();
		if (!q || departmentGroups.length !== 1) return new Set();
		const [, deptRows] = departmentGroups[0];
		return new Set(
			deptRows
				.filter((r) => r.roleString.toLowerCase().includes(q) || r.rawEmployees.some((e) => hrisEmployeeName(e).toLowerCase().includes(q)))
				.map((r) => r.roleString),
		);
	}, [departmentGroups, search]);

	// Single-expand: only one department accordion open at a time. Default to the first group
	// so the pane is never blank on first render.
	const [expandedDept, setExpandedDept] = useState<string | null>(() => {
		const first = departmentGroups[0];
		return first ? first[0] || NO_DEPT_KEY : null;
	});

	// When filtering narrows the list to a single department, auto-open it so the user doesn't
	// have to click. Runs whenever the visible groups change (e.g. as the user types in search).
	useEffect(() => {
		if (departmentGroups.length !== 1) return;
		const onlyKey = departmentGroups[0][0] || NO_DEPT_KEY;
		setExpandedDept(onlyKey);
	}, [departmentGroups]);

	if (rowsWithEmployees.length === 0) {
		return (
			<Box sx={{ p: 2 }}>
				<Typography variant="body2" color="text.secondary">
					{t("hris.mapping.rawEmployees.empty")}
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "start",
				justifyContent: "start",
				flexDirection: "column",
				gap: 1,
				height: "100%",
				overflow: "hidden",
			}}
			data-testid="hris-mapping-raw-employees-pane"
		>
			<Box sx={{ width: "100%", pr: 1.5 }}>
				<PaneHeader
					title={t("hris.mapping.alignment.editPane.title")}
					hint={t("hris.mapping.alignment.editPane.hint")}
					dataTestId="hris-mapping-edit-pane-header"
				/>
			</Box>
			{/*
			 * Pane-level summary of the fourth per-employee icon, above the search box so it describes
			 * the whole pane rather than the current query. Read-only (DAT-834) — both fixes are
			 * elsewhere: this pane's job picker, or the Jobs tab's enabled-jobs list.
			 */}
			{markedJobNotEnabledCount > 0 && (
				// `pr: 1.5` is the pane's convention, not a tuned value: the header above and the search
				// field below both pay it so their right edge lines up with the scrolling list, which
				// reserves that gutter for its scrollbar.
				<Box sx={{ width: "100%", pr: 1.5 }}>
					<SystemBanner
						tone="warning"
						icon={<WorkOffOutlined />}
						title={t("hris.mapping.rawEmployees.jobNotEnabled.title", { count: markedJobNotEnabledCount })}
						description={
							unmarkedJobNotEnabledCount > 0
								? `${t("hris.mapping.rawEmployees.jobNotEnabled.body")} ${t("hris.mapping.rawEmployees.jobNotEnabled.alsoElsewhere", {
										count: unmarkedJobNotEnabledCount,
									})}`
								: t("hris.mapping.rawEmployees.jobNotEnabled.body")
						}
						dataTestId="hris-mapping-job-not-enabled-banner"
					/>
				</Box>
			)}
			<SystemValidationTextField
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				placeholder={t("hris.mapping.rawEmployees.search")}
				reserveHeightForHelperText={false}
				onClear={() => setSearch("")}
				formControlSx={{ width: "100%", pr: 1.5 }}
				data-testid="hris-mapping-raw-employees-search"
			/>
			{/* flex:1 + minHeight:0 so the list scrolls within the pane rather than pushing it taller. */}
			<Box sx={{ width: "100%", flex: 1, minHeight: 0, overflow: "auto", p: 1, pl: 0.1, pr: 1.5 }}>
				{departmentGroups.map(([dept, deptRows]) => {
					const employeeCount = deptRows.reduce((acc, r) => acc + r.rawEmployees.length, 0);
					// Active + matched employees only; Don't-Sync rows contribute 0 (see countSyncingEmployees).
					const deptSyncedCount = deptRows.reduce((acc, r) => acc + countSyncingEmployees(r, statsByEmployeeId), 0);
					const hasOrphans = deptRows.some((r) => r.isOrphan);
					const key = dept || NO_DEPT_KEY;
					return (
						<Accordion
							key={key}
							expanded={expandedDept === key}
							onChange={(_, isOpen) => setExpandedDept(isOpen ? key : null)}
							elevation={1}
							data-testid={`hris-mapping-dept-group-${dept || "no-dept"}`}
							sx={hasOrphans ? { border: "1px solid", borderColor: (theme) => alpha(theme.palette.warning.light, 0.5) } : undefined}
						>
							<AccordionSummary expandIcon={<ExpandMore />} sx={{ "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 } }}>
								<Domain fontSize="small" sx={{ color: "text.secondary" }} />
								<Typography sx={{ fontWeight: 600 }}>{dept || "—"}</Typography>
								{/* Count + status pushed right so the dept name leads; same status icon + tooltip as per-role rows. */}
								<Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.75 }}>
									<Typography variant="caption" sx={{ color: "text.secondary" }}>
										{t("hris.mapping.rawEmployees.employeeCount", { count: employeeCount })}
									</Typography>
									<SyncStatusIcon syncedCount={deptSyncedCount} totalCount={employeeCount} testId={`hris-mapping-dept-sync-status-${dept || "no-dept"}`} />
								</Box>
							</AccordionSummary>
							<AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
								{deptRows.map((row) => (
									<RoleStringRowItem
										key={`${dept}::${row.roleString}`}
										row={row}
										jobs={jobs}
										isPending={pendingKeys.has(row.roleString)}
										onJobChange={onJobChange}
										onAdditionalJobsChange={onAdditionalJobsChange}
										statsByEmployeeId={statsByEmployeeId}
										jobNotEnabledByEmployeeId={jobNotEnabledByEmployeeId}
										autoExpand={autoExpandedRoleStrings.has(row.roleString)}
									/>
								))}
							</AccordionDetails>
						</Accordion>
					);
				})}
			</Box>
		</Box>
	);
};
